import { useGLTF } from '@react-three/drei'
import { DRACO_DECODER_PATH, isV2ModelPath } from 'features/Procedure/hooks/useEvaluateModel'
import { V2_SCENE_OFFSET } from 'features/Procedure/procedure-model/body/body.constants'
import { useFrame, useThree } from '@react-three/fiber'
import { FreeLine, StraightLine } from 'features/Procedure/procedure-model/lines'
import { Pin } from 'features/Procedure/procedure-model/pin'
import { XPin } from 'features/Procedure/procedure-model/x-pin'
import { ProcedureInitialState } from 'features/Procedure/state/procedure.slice'
import { DrawMode, type DrawingElement } from 'features/Procedure/state/procedure.type'
import { normalizeStrokeWidthToFreeLinePreset } from 'features/Procedure/utils/drawingElementFactory'
import { useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import type { Group } from 'three'
import * as THREE from 'three'
import { Vector3 } from 'three'
import { OrbitControls } from 'three-stdlib'

type BodyData = {
  drawingElements: DrawingElement[]
  modelPath: string
  skinColor?: string
}

type DualBodyViewProps = {
  leftBody: BodyData
  rightBody: BodyData
  onFirstRender?: () => void
}

const BODY_SEPARATION = 80

/** ~28fps cap while orbiting — dual view pays 2× render per tick, so this cuts GPU load during drag. */
const ORBIT_INVALIDATE_MIN_MS = 1000 / 28

const toLambertSkinMaterial = (
  mat: THREE.MeshStandardMaterial | THREE.MeshPhysicalMaterial,
  skinColorObj: THREE.Color | null,
) => {
  const color = skinColorObj ? skinColorObj.clone() : mat.color.clone()
  const emissive = skinColorObj ? skinColorObj.clone().multiplyScalar(0.2) : mat.emissive.clone()
  const lambert = new THREE.MeshLambertMaterial({
    color,
    emissive,
    map: mat.map ?? undefined,
    side: THREE.DoubleSide,
  })

  return lambert
}

const BodyModel = ({
  modelPath,
  skinColor,
  offsetX,
}: {
  modelPath: string
  skinColor?: string
  offsetX: number
}) => {
  const groupRef = useRef<Group>(null)
  const gltf = useGLTF(modelPath, DRACO_DECODER_PATH)
  const scene = useMemo(() => gltf.scene.clone(), [gltf.scene])
  const ownedMaterialsRef = useRef<THREE.Material[]>([])

  useEffect(() => {
    if (!scene) return

    const disposePrevious = () => {
      for (const mat of ownedMaterialsRef.current) {
        mat.dispose()
      }
      ownedMaterialsRef.current = []
    }

    disposePrevious()

    scene.position.set(0, 0, 0)
    scene.scale.setScalar(60)

    const box = new THREE.Box3().setFromObject(scene)
    const center = box.getCenter(new THREE.Vector3())
    scene.position.sub(center)
    if (isV2ModelPath(modelPath)) {
      scene.position.x += V2_SCENE_OFFSET.x
      scene.position.y += V2_SCENE_OFFSET.y
      scene.position.z += V2_SCENE_OFFSET.z
    }
    scene.position.x += offsetX

    // v2 models ship with baked skin shading; only override color for legacy models.
    const skinColorObj = skinColor && !isV2ModelPath(modelPath) ? new THREE.Color(skinColor) : null

    scene.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.visible = true
        child.castShadow = false
        child.receiveShadow = false

        if (child.geometry) {
          if (!child.geometry.boundingBox) child.geometry.computeBoundingBox()
          if (!child.geometry.boundingSphere) child.geometry.computeBoundingSphere()
          child.updateMatrix()
          child.updateMatrixWorld(true)
        }

        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material = child.material.map((mat) => {
              if (
                mat instanceof THREE.MeshStandardMaterial ||
                mat instanceof THREE.MeshPhysicalMaterial
              ) {
                const owned = toLambertSkinMaterial(mat, skinColorObj)
                ownedMaterialsRef.current.push(owned)
                return owned
              }
              const c = mat.clone()
              c.side = THREE.DoubleSide
              ownedMaterialsRef.current.push(c)
              return c
            })
          } else if (
            child.material instanceof THREE.MeshStandardMaterial ||
            child.material instanceof THREE.MeshPhysicalMaterial
          ) {
            const owned = toLambertSkinMaterial(child.material, skinColorObj)
            ownedMaterialsRef.current.push(owned)
            child.material = owned
          } else {
            const owned = child.material.clone()
            owned.side = THREE.DoubleSide
            ownedMaterialsRef.current.push(owned)
            child.material = owned
          }
        }
      }
    })

    scene.updateMatrix()
    scene.updateMatrixWorld(true)

    return disposePrevious
  }, [scene, skinColor, offsetX])

  return (
    <>
      {}
      <primitive object={scene} ref={groupRef} />
    </>
  )
}

const DrawingElements = ({
  elements,
  offsetX,
  bodyScene,
}: {
  elements: DrawingElement[]
  offsetX: number
  bodyScene: THREE.Object3D
}) => {
  return (
    <group position-x={offsetX}>
      {elements.map((element) => {
        const key = `${element.id}-${element.timestamp.toString()}-${offsetX}`

        if (element.drawMode === DrawMode.PIN) {
          return (
            <Pin
              key={key}
              element={element}
              isSelected={false}
              isFromSelectedProcedure={false}
              showElementIds={true}
              inactiveOpacity={1}
              useDepthTest={true}
            />
          )
        }

        if (element.drawMode === DrawMode.STRAIGHT_LINE || element.drawMode === DrawMode.MEASURE) {
          const points = element.points.map((point) => new Vector3(point.x, point.y, point.z))
          const hasValidCamera =
            element.camera?.position &&
            typeof element.camera.position.x === 'number' &&
            typeof element.camera.position.y === 'number' &&
            typeof element.camera.position.z === 'number'
          const cameraPos = hasValidCamera
            ? new Vector3(
                element.camera!.position.x,
                element.camera!.position.y,
                element.camera!.position.z,
              )
            : undefined
          return (
            <StraightLine
              key={key}
              startPoint={points[0]}
              endPoint={points[1]}
              color={element.color}
              lineWidth={normalizeStrokeWidthToFreeLinePreset(element.size)}
              bodyMesh={bodyScene}
              cameraPosition={cameraPos}
              showMeasurement={element.drawMode === DrawMode.MEASURE}
              showDirection={element.drawMode === DrawMode.STRAIGHT_LINE}
              isSelected={false}
              isFromSelectedProcedure={true}
              showElementIds={true}
              description={element.description}
              useDepthTest={true}
              readOnly
            />
          )
        }

        if (element.drawMode === DrawMode.FREE_LINE) {
          const hasValidCamera =
            element.camera?.position &&
            typeof element.camera.position.x === 'number' &&
            typeof element.camera.position.y === 'number' &&
            typeof element.camera.position.z === 'number'
          const cameraPos = hasValidCamera
            ? new Vector3(
                element.camera!.position.x,
                element.camera!.position.y,
                element.camera!.position.z,
              )
            : undefined
          return (
            <FreeLine
              key={key}
              points={element.points}
              color={element.color}
              lineWidth={element.size}
              bodyMesh={bodyScene}
              cameraPosition={cameraPos}
              isSelected={false}
              isFromSelectedProcedure={true}
              showElementIds={true}
              description={element.description}
              descriptionPosition={element.descriptionPosition}
              useDepthTest={true}
              readOnly
            />
          )
        }

        if (element.drawMode === DrawMode.X) {
          return (
            <XPin
              key={key}
              element={element}
              bodyMesh={bodyScene}
              isSelected={false}
              isFromSelectedProcedure={false}
              showElementIds={true}
              inactiveOpacity={1}
              useDepthTest={true}
            />
          )
        }

        return null
      })}
    </group>
  )
}

export const DualBodyView = ({ leftBody, rightBody, onFirstRender }: DualBodyViewProps) => {
  const { gl, scene, size, invalidate } = useThree()
  const controlsRef = useRef<OrbitControls | null>(null)
  const positionOffsetRef = useRef(new THREE.Vector3())
  const targetOffsetRef = useRef(new THREE.Vector3())
  const currentRightTargetRef = useRef(new THREE.Vector3())
  const orbitInvalidateRef = useRef({ last: 0, raf: 0 })
  const hasCalledFirstRenderRef = useRef(false)

  const leftGltf = useGLTF(leftBody.modelPath, DRACO_DECODER_PATH)
  const rightGltf = useGLTF(rightBody.modelPath, DRACO_DECODER_PATH)

  const leftScene = useMemo(() => {
    const s = leftGltf.scene.clone()
    s.scale.setScalar(60)
    const box = new THREE.Box3().setFromObject(s)
    const center = box.getCenter(new THREE.Vector3())
    s.position.sub(center)
    if (isV2ModelPath(leftBody.modelPath)) {
      s.position.x += V2_SCENE_OFFSET.x
      s.position.y += V2_SCENE_OFFSET.y
      s.position.z += V2_SCENE_OFFSET.z
    }
    s.updateMatrix()
    s.updateMatrixWorld(true)
    return s
  }, [leftGltf.scene, leftBody.modelPath])

  const rightScene = useMemo(() => {
    const s = rightGltf.scene.clone()
    s.scale.setScalar(60)
    const box = new THREE.Box3().setFromObject(s)
    const center = box.getCenter(new THREE.Vector3())
    s.position.sub(center)
    if (isV2ModelPath(rightBody.modelPath)) {
      s.position.x += V2_SCENE_OFFSET.x
      s.position.y += V2_SCENE_OFFSET.y
      s.position.z += V2_SCENE_OFFSET.z
    }
    s.updateMatrix()
    s.updateMatrixWorld(true)
    return s
  }, [rightGltf.scene, rightBody.modelPath])

  const leftCamera = useMemo(() => {
    const cam = new THREE.PerspectiveCamera(40, 1, 0.1, 1000)
    cam.position.set(
      ProcedureInitialState.scene.position.x - BODY_SEPARATION / 2,
      ProcedureInitialState.scene.position.y,
      ProcedureInitialState.scene.position.z,
    )
    return cam
  }, [])

  const rightCamera = useMemo(() => {
    const cam = new THREE.PerspectiveCamera(40, 1, 0.1, 1000)
    cam.position.set(
      ProcedureInitialState.scene.position.x + BODY_SEPARATION / 2,
      ProcedureInitialState.scene.position.y,
      ProcedureInitialState.scene.position.z,
    )
    return cam
  }, [])

  const leftTarget = useMemo(
    () =>
      new THREE.Vector3(
        ProcedureInitialState.scene.target.x - BODY_SEPARATION / 2,
        ProcedureInitialState.scene.target.y,
        ProcedureInitialState.scene.target.z,
      ),
    [],
  )

  const rightTarget = useMemo(
    () =>
      new THREE.Vector3(
        ProcedureInitialState.scene.target.x + BODY_SEPARATION / 2,
        ProcedureInitialState.scene.target.y,
        ProcedureInitialState.scene.target.z,
      ),
    [],
  )

  useLayoutEffect(() => {
    const canvas = gl.domElement

    const controls = new OrbitControls(leftCamera, canvas)
    controls.enablePan = true
    controls.enableZoom = true
    controls.enableRotate = true
    controls.rotateSpeed = 0.5
    controls.zoomSpeed = 0.5
    controls.panSpeed = 0.5
    controls.minDistance = 0.5
    controls.maxDistance = 200
    controls.maxPolarAngle = Math.PI
    controls.target.copy(leftTarget)
    leftCamera.lookAt(leftTarget)
    leftCamera.updateMatrixWorld()
    rightCamera.lookAt(rightTarget)
    rightCamera.updateMatrixWorld()
    controls.update()

    const throttle = orbitInvalidateRef.current
    const scheduleInvalidate = () => {
      const now = performance.now()
      if (now - throttle.last >= ORBIT_INVALIDATE_MIN_MS) {
        throttle.last = now
        invalidate()
        return
      }
      if (!throttle.raf) {
        throttle.raf = requestAnimationFrame(() => {
          throttle.raf = 0
          throttle.last = performance.now()
          invalidate()
        })
      }
    }

    const onEnd = () => {
      if (throttle.raf) {
        cancelAnimationFrame(throttle.raf)
        throttle.raf = 0
      }
      throttle.last = 0
      invalidate()
    }

    controls.addEventListener('change', scheduleInvalidate)
    controls.addEventListener('end', onEnd)
    controlsRef.current = controls

    invalidate()

    return () => {
      if (throttle.raf) cancelAnimationFrame(throttle.raf)
      throttle.raf = 0
      controls.removeEventListener('change', scheduleInvalidate)
      controls.removeEventListener('end', onEnd)
      controls.dispose()
      controlsRef.current = null
    }
  }, [gl.domElement, leftCamera, rightCamera, leftTarget, rightTarget, invalidate])

  useEffect(() => {
    invalidate()
  }, [leftBody.modelPath, rightBody.modelPath, invalidate])

  useFrame(() => {
    if (!hasCalledFirstRenderRef.current) {
      hasCalledFirstRenderRef.current = true
      onFirstRender?.()
    }

    if (controlsRef.current) {
      controlsRef.current.update()

      positionOffsetRef.current.subVectors(leftCamera.position, controlsRef.current.target)
      targetOffsetRef.current.subVectors(controlsRef.current.target, leftTarget)
      currentRightTargetRef.current.copy(rightTarget).add(targetOffsetRef.current)

      rightCamera.position.copy(currentRightTargetRef.current).add(positionOffsetRef.current)
      rightCamera.lookAt(currentRightTargetRef.current)
      rightCamera.updateMatrixWorld()
    }

    const halfWidth = Math.floor(size.width / 2)

    leftCamera.aspect = halfWidth / size.height
    leftCamera.updateProjectionMatrix()
    gl.setViewport(0, 0, halfWidth, size.height)
    gl.setScissor(0, 0, halfWidth, size.height)
    gl.setScissorTest(true)
    gl.render(scene, leftCamera)

    rightCamera.aspect = halfWidth / size.height
    rightCamera.updateProjectionMatrix()
    gl.setViewport(halfWidth, 0, size.width - halfWidth, size.height)
    gl.setScissor(halfWidth, 0, size.width - halfWidth, size.height)
    gl.render(scene, rightCamera)
  }, 1)

  return (
    <>
      <BodyModel
        modelPath={leftBody.modelPath}
        skinColor={leftBody.skinColor}
        offsetX={-BODY_SEPARATION / 2}
      />
      <DrawingElements
        elements={leftBody.drawingElements}
        offsetX={-BODY_SEPARATION / 2}
        bodyScene={leftScene}
      />

      <BodyModel
        modelPath={rightBody.modelPath}
        skinColor={rightBody.skinColor}
        offsetX={BODY_SEPARATION / 2}
      />
      <DrawingElements
        elements={rightBody.drawingElements}
        offsetX={BODY_SEPARATION / 2}
        bodyScene={rightScene}
      />

      {}
      <ambientLight intensity={0.4} />
      {}
      <directionalLight position={[10, 10, 5]} intensity={1} />
      {}
      <directionalLight position={[-10, -10, -5]} intensity={0.5} />
    </>
  )
}
